import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Save, Sparkles, Plus, X, Brain } from "lucide-react";
import { getProfile, updateProfile, learnProfile } from "../lib/api";
import StyleDNA from "../components/StyleDNA";
import { toast } from "sonner";

export default function ProfileView() {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  const [learning, setLearning] = useState(false);

  useEffect(() => { getProfile().then(setP).catch(() => {}); }, []);

  const set = (k, v) => setP((cur) => ({ ...cur, [k]: v }));
  const setList = (k, i, v) => setP((cur) => { const a = [...(cur[k] || [])]; a[i] = v; return { ...cur, [k]: a }; });
  const addItem = (k) => setP((cur) => ({ ...cur, [k]: [...(cur[k] || []), ""] }));
  const delItem = (k, i) => setP((cur) => ({ ...cur, [k]: cur[k].filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const clean = {
        summary: p.summary,
        scoring_notes: p.scoring_notes,
        patterns: (p.patterns || []).map((s) => s.trim()).filter(Boolean),
        catchphrases: (p.catchphrases || []).map((s) => s.trim()).filter(Boolean),
      };
      const res = await updateProfile(clean);
      setP(res);
      toast.success("Humor profile saved — future clips scored in your voice");
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const relearn = async () => {
    setLearning(true);
    try {
      const res = await learnProfile();
      setP(res);
      toast.success("Re-learned your style from your mined shows");
    } catch (e) { toast.error(e?.response?.data?.detail || "Re-learn failed"); } finally { setLearning(false); }
  };

  if (!p) return <div className="flex h-96 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="profile-view" className="mx-auto max-w-4xl space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-border pb-6">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-primary">Persistent Memory</p>
          <h2 className="mt-1 flex items-center gap-3 font-display text-4xl tracking-wide text-white sm:text-5xl">
            <Brain className="h-8 w-8 text-accent" /> HUMOR PROFILE
          </h2>
          <p className="mt-2 max-w-xl font-body text-sm text-muted-foreground">
            This is the lens every future clip gets scored through. Edit it, or re-learn it from your mined shows.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {p.auto_generated && <span className="font-mono text-[10px] uppercase tracking-widest text-accent">auto-learned</span>}
          <button
            data-testid="relearn-btn"
            onClick={relearn}
            disabled={learning}
            className="flex h-10 items-center gap-2 border border-border px-4 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground transition-colors hover:border-accent hover:text-accent disabled:opacity-60"
          >
            {learning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            Re-learn from clips
          </button>
        </div>
      </div>

      <StyleDNA />

      <Field label="Style Summary">
        <textarea
          data-testid="profile-summary"
          value={p.summary || ""}
          onChange={(e) => set("summary", e.target.value)}
          rows={4}
          className="w-full resize-none rounded-none border border-border bg-background p-3 text-sm text-white focus:outline-none focus:ring-1 focus:ring-accent"
        />
      </Field>

      <ListField label="Signature Patterns" items={p.patterns || []} k="patterns"
        setList={setList} addItem={addItem} delItem={delItem} testid="patterns" />

      <Field label="Catchphrases (used as on-screen hooks)">
        <div className="flex flex-wrap gap-2">
          {(p.catchphrases || []).map((c, i) => (
            <span key={i} className="flex items-center gap-1.5 border border-accent/40 bg-accent/10 px-3 py-1.5 font-mono text-xs text-accent">
              <input
                data-testid={`catchphrase-${i}`}
                value={c}
                onChange={(e) => setList("catchphrases", i, e.target.value)}
                className="w-auto bg-transparent text-accent focus:outline-none"
                style={{ width: `${Math.max(4, c.length)}ch` }}
              />
              <button onClick={() => delItem("catchphrases", i)} className="hover:text-white"><X className="h-3 w-3" /></button>
            </span>
          ))}
          <button
            data-testid="add-catchphrase"
            onClick={() => addItem("catchphrases")}
            className="flex items-center gap-1 border border-dashed border-border px-3 py-1.5 font-mono text-xs text-muted-foreground hover:border-accent hover:text-accent"
          >
            <Plus className="h-3 w-3" /> add
          </button>
        </div>
      </Field>

      <Field label="Scoring Rules">
        <textarea
          data-testid="profile-scoring"
          value={p.scoring_notes || ""}
          onChange={(e) => set("scoring_notes", e.target.value)}
          rows={4}
          className="w-full resize-none rounded-none border border-border bg-background p-3 text-sm text-white focus:outline-none focus:ring-1 focus:ring-accent"
        />
      </Field>

      <div className="sticky bottom-4 flex justify-end">
        <motion.button
          whileTap={{ scale: 0.97 }}
          data-testid="save-profile-btn"
          onClick={save}
          disabled={saving}
          className="flex items-center gap-2 bg-accent px-8 py-3 text-sm font-semibold uppercase tracking-[0.15em] text-accent-foreground shadow-lg transition-colors hover:bg-accent/90 disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save Profile
        </motion.button>
      </div>
    </motion.div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</label>
    {children}
  </div>
);

const ListField = ({ label, items, k, setList, addItem, delItem, testid }) => (
  <Field label={label}>
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} className="flex items-start gap-2">
          <span className="mt-3 h-1.5 w-1.5 shrink-0 bg-primary" />
          <textarea
            data-testid={`${testid}-${i}`}
            value={it}
            onChange={(e) => setList(k, i, e.target.value)}
            rows={2}
            className="w-full resize-none rounded-none border border-border bg-background p-2.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-accent"
          />
          <button onClick={() => delItem(k, i)} className="mt-2 text-muted-foreground hover:text-primary"><X className="h-4 w-4" /></button>
        </div>
      ))}
      <button
        data-testid={`add-${testid}`}
        onClick={() => addItem(k)}
        className="flex items-center gap-1 border border-dashed border-border px-3 py-2 font-mono text-xs text-muted-foreground hover:border-accent hover:text-accent"
      >
        <Plus className="h-3 w-3" /> add pattern
      </button>
    </div>
  </Field>
);
